import type { WithDbId } from "adl-gen/common/db";
import type { ExistingProductsResp } from "adl-gen/ferovinum/app/api";
import type { ProductQuantity } from "adl-gen/ferovinum/app/common";
import type { Product } from "adl-gen/ferovinum/app/db";
import type { ParseResult } from "papaparse";
import { CsvRowParser } from "utils/csv-row-parser";
import type { RowIdentifierAndErrors, WithCsvRowNumber } from "utils/csv-utils";
import { numberField, stringField } from "utils/data-field/data-field-builder";
import { makeObjectDef } from "utils/data-field/object-field-def";
import { numberOfUnitsForUnitType } from "utils/model-utils";
import { ensure, filterValid } from "utils/null-and-undefined-utils";

export type ProductQuantityCsvRowData = {
  productCode: string;
  numberOfUnits?: number;
};

export interface ProductQuantityUploadResult {
  success: WithCsvRowNumber<ProductQuantity>[];
  nonExistingProductCodes: string[];
  errors: WithCsvRowNumber<RowIdentifierAndErrors>[];
}

export class ProductQuantityCsvProcessor {
  private readonly productExtractor: (
    productCodes: string[],
  ) => Promise<ExistingProductsResp>;
  private readonly postUploadValidator?: (
    uploadResult: ProductQuantityUploadResult,
  ) => Promise<ProductQuantityUploadResult>;

  constructor({
    productExtractor,
    postUploadValidator,
  }: {
    productExtractor: (productCodes: string[]) => Promise<ExistingProductsResp>;
    postUploadValidator?: (
      uploadResult: ProductQuantityUploadResult,
    ) => Promise<ProductQuantityUploadResult>;
  }) {
    this.productExtractor = productExtractor;
    this.postUploadValidator = postUploadValidator;
  }

  public async parseCSVToProductLineItems(
    parseResult: ParseResult<string[]>,
  ): Promise<ProductQuantityUploadResult> {
    const { data, errors: parseErrors } = parseResult;
    const rowsWithoutHeader = data.slice(1);

    const headers = data[0];
    const csvParser = new ProductQuantityCsvParser(headers);
    const productCodes = rowsWithoutHeader.map((row) =>
      csvParser.getProductCode(row),
    );
    const analyzedProducts = await this.productExtractor(
      filterValid(productCodes),
    );

    // create a map of product code to product: This includes products with and without deal history
    // Each rec can decide if it will take the product into account or not
    const existingProductMap = new Map<string, WithDbId<Product>>(
      [...analyzedProducts.foundProducts].map((product) => [
        product.value.code,
        product,
      ]),
    );

    const result: ProductQuantityUploadResult = {
      success: [],
      errors: [],
      nonExistingProductCodes: analyzedProducts.missingProductCodes,
    };

    rowsWithoutHeader.forEach((row, index) => {
      const rowNumber = index + 2;
      const productCode = csvParser.getProductCode(row);
      const validationErrors = csvParser.validate(row);

      const rawQuantity = csvParser.getRawQuantity(row);
      const errors: string[] = [];

      if (!productCode) errors.push("Product Code is missing.");
      if (rawQuantity === undefined) errors.push("Raw Quantity is missing.");
      if (validationErrors.length > 0) errors.push(...validationErrors);

      const parseError = parseErrors.find((e) => e.row === rowNumber);
      if (parseError) errors.push(parseError.message);

      if (errors.length > 0) {
        result.errors.push({
          value: {
            rowIdentifier: `Product code: ${productCode}` || `Row: ${index}`,
            errors,
          },
          rowNumber,
        });
        return;
      }

      const product = existingProductMap.get(ensure(productCode));
      if (product) {
        const value = csvParser.createProductQuantity(row, product);
        result.success.push({ value, rowNumber });
      }
    });

    return this.postUploadValidator
      ? await this.postUploadValidator(result)
      : result;
  }
}

const PRODUCT_QUANTITY_CSV_ROW_DATA_DEF =
  makeObjectDef<ProductQuantityCsvRowData>({
    productCode: stringField("Product code").required(),
    numberOfUnits: numberField("Quantity", { range: { min: 0 } }).optional(),
  });

export class ProductQuantityCsvParser {
  private readonly rowParser: CsvRowParser<ProductQuantityCsvRowData>;

  constructor(headers: string[]) {
    this.rowParser = CsvRowParser.make(
      PRODUCT_QUANTITY_CSV_ROW_DATA_DEF,
      headers,
    );
  }

  public getProductCode(rowData: Readonly<string[]>): string | null {
    return this.rowParser.castData(rowData, "productCode") ?? null;
  }

  public getRawQuantity(rowData: Readonly<string[]>): number | null {
    return this.rowParser.castData(rowData, "numberOfUnits") ?? null;
  }

  public validate(rowData: Readonly<string[]>): string[] {
    return this.rowParser.checkForErrors(rowData);
  }
  public createProductQuantity(
    rowData: Readonly<string[]>,
    product: WithDbId<Product>,
  ): ProductQuantity {
    const { numberOfUnits } = this.rowParser.parse(rowData);

    return {
      product: product,
      quantity: numberOfUnitsForUnitType(
        product.value.unitType,
        numberOfUnits ?? 0,
      ),
    };
  }
}
